// src/hooks/useOfferHandlers.js
/**
 * Custom hook for offer-related handlers in messaging
 */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { respondToOffer } from '../utils/offerEngine';
import * as offersService from '../services/offersService';
import { sendMessage } from '../services/chatService';
import { useToast } from '../contexts/ToastContext';
import { errorDev } from '../utils/logger.js';

export default function useOfferHandlers(currentUser, chatId, messages, setMessages, offerCache, setOfferCache) {
  const toast = useToast();
  const { t } = useTranslation(['inbox', 'common']);
  // Helper: update local message offer status
  const updateLocalMessageOfferStatus = useCallback((messageId, newStatus) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (!m || m.id !== messageId) return m;
        const extra = { ...(m.extra || {}) };
        extra.offerStatus = newStatus;
        return { ...m, extra };
      })
    );
  }, [setMessages]);

  // Handle accept offer
  const handleAcceptOffer = useCallback(async (msg) => {
    const offerId =
      msg?.extra?.offerId ||
      msg?.offerId ||
      msg?.extra?.offerRefId ||
      msg?.offerRefId;
    if (!offerId) {
      toast.warning('Offer details are missing. Please refresh and try again.');
      return;
    }
    if (!currentUser) {
      toast.warning('Please sign in to continue.');
      return;
    }

    const prevMessages = messages;
    updateLocalMessageOfferStatus(msg.id, 'accepted');

    try {
      await respondToOffer(offerId, 'accepted', currentUser.uid);
      setOfferCache((prev) => {
        const next = { ...prev };
        if (next[offerId])
          next[offerId] = { ...next[offerId], status: 'accepted' };
        return next;
      });
    } catch (err) {
      errorDev("Operation failed:", err);
      setMessages(prevMessages);
      toast.error('Could not accept this offer right now. Please try again.');
    }
  }, [currentUser, messages, updateLocalMessageOfferStatus, setMessages, setOfferCache, toast]);

  // Handle decline offer
  const handleDeclineOffer = useCallback(async (msg) => {
    const offerId =
      msg?.extra?.offerId ||
      msg?.offerId ||
      msg?.extra?.offerRefId ||
      msg?.offerRefId;
    if (!offerId) {
      toast.warning('Offer details are missing. Please refresh and try again.');
      return;
    }
    if (!currentUser) {
      toast.warning('Please sign in to continue.');
      return;
    }

    const prevMessages = messages;
    updateLocalMessageOfferStatus(msg.id, 'declined');

    try {
      await respondToOffer(offerId, 'declined', currentUser.uid);
      setOfferCache((prev) => {
        const next = { ...prev };
        if (next[offerId])
          next[offerId] = { ...next[offerId], status: 'declined' };
        return next;
      });
    } catch (err) {
      errorDev("Operation failed:", err);
      setMessages(prevMessages);
      toast.error('Could not decline this offer right now. Please try again.');
    }
  }, [currentUser, messages, updateLocalMessageOfferStatus, setMessages, setOfferCache, toast]);

  // Handle payment success
  const onPaymentSuccess = useCallback(async (res) => {
    try {
      if (currentUser && chatId && res?.changed) {
        const statusText = res?.action === 'sent'
          ? t('inbox:sellerSentSystemMessage', 'Seller has sent the item. Please confirm when it arrives.')
          : res?.action === 'received'
            ? t('inbox:buyerReceivedSystemMessage', 'Buyer confirmed the item was received. Transaction completed.')
            : null;

        if (!statusText) return;

        await sendMessage({
          chatId,
          senderId: currentUser.uid,
          text: statusText,
          type: 'system',
          extra: { offerId: res?.offerId || null, status: res?.status || null, action: res?.action || null },
        });
      }
    } catch (err) {
      // silent
    errorDev("Operation failed:", err);
    }
  }, [currentUser, chatId, t]);

  return {
    handleAcceptOffer,
    handleDeclineOffer,
    onPaymentSuccess,
    updateLocalMessageOfferStatus,
  };
}
